import { useLayoutEffect, useRef, useState, type ComponentProps } from 'react';
import { Textarea } from '@/components/ui/Textarea';
import {
  REPORT_TEXT_BOX_MAX_PX,
  measureGrowingTextarea,
} from '@/lib/patientReports/growingTextarea';
import { cn } from '@/utils/cn';

/**
 * The report tool's Tekst field. It opens at the usual field height and
 * grows with the text already there or typed in, until the cap, then scrolls.
 */
export function GrowingTextarea({
  value,
  className,
  style,
  ...props
}: ComponentProps<typeof Textarea>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [scroll, setScroll] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const apply = () => {
      const next = measureGrowingTextarea(node);
      setHeight((current) => (current === next.height ? current : next.height));
      setScroll((current) => (current === next.scroll ? current : next.scroll));
    };

    apply();

    if (typeof ResizeObserver === 'undefined') return;
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      apply();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [value]);

  return (
    <Textarea
      {...props}
      ref={ref}
      value={value}
      className={cn('min-h-28 resize-none', className)}
      style={{
        ...style,
        maxHeight: REPORT_TEXT_BOX_MAX_PX,
        height: height && height > 0 ? height : undefined,
        overflowY: scroll ? 'auto' : 'hidden',
      }}
    />
  );
}
